import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OTPVerification from './OTPVerification';

type RegistrationStep = 'type' | 'contact' | 'verify' | 'complete' | 'corporate-docs';
type CustomerType = 'individual' | 'corporate';

interface RegistrationData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  customerType: CustomerType;
  companyName: string;
  companyRegistrationNumber: string;
  companyAddress: string;
}

const CustomerRegister: React.FC = () => {
  const [step, setStep] = useState<RegistrationStep>('type');
  const [formData, setFormData] = useState<RegistrationData>({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    customerType: 'individual',
    companyName: '',
    companyRegistrationNumber: '',
    companyAddress: '',
  });
  const [sessionId, setSessionId] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationDocFile, setRegistrationDocFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const regDocInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleTypeSelect = (type: CustomerType) => {
    setFormData({ ...formData, customerType: type });
    setStep('contact');
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/auth/pre-registration/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send verification codes');
      }

      setSessionId(data.session_id);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to start registration');
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = () => {
    setStep('complete');
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.customerType === 'corporate') {
      if (!formData.companyName || !formData.companyRegistrationNumber || !formData.companyAddress) {
        setError('Please fill in all company details');
        return;
      }
    }

    setLoading(true);

    try {
      const response = await fetch('/auth/pre-registration/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          password: formData.password,
          customer_type: formData.customerType,
          company_name: formData.customerType === 'corporate' ? formData.companyName : null,
          company_registration_number: formData.customerType === 'corporate' ? formData.companyRegistrationNumber : null,
          company_address: formData.customerType === 'corporate' ? formData.companyAddress : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      if (formData.customerType === 'corporate') {
        setStep('corporate-docs');
      } else {
        await login(data.user.email, formData.password);
        navigate('/customer-dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!registrationDocFile || !logoFile) {
      setError('Please upload both required documents');
      return;
    }

    setLoading(true);
    setUploadProgress('Uploading documents...');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('registration_doc', registrationDocFile);
      formDataUpload.append('company_logo', logoFile);

      const token = localStorage.getItem('access_token');
      const response = await fetch('/auth/corporate/upload-documents', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to upload documents');
      }

      setUploadProgress('Documents uploaded successfully!');
      
      setTimeout(() => {
        navigate('/pending-approval');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to upload documents');
      setUploadProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await fetch(
        `/auth/pre-registration/resend-otp?session_id=${sessionId}`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }
    } catch (err: any) {
      console.error('Resend error:', err);
    }
  };

  if (step === 'type') {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx border border-gray-100 max-w-md mx-auto">
        <div className="mb-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Create Account</h3>
          <p className="text-gray-600">Choose your account type to get started</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => handleTypeSelect('individual')}
            className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-sky-500 hover:bg-sky-50 transition-all group text-left"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">👤</div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 group-hover:text-sky-600">
                  Individual Account
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  For personal deliveries. Quick registration and instant access.
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleTypeSelect('corporate')}
            className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all group text-left"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">🏢</div>
              <div>
                <h4 className="font-semibold text-lg text-gray-900 group-hover:text-orange-600">
                  Corporate Account
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  For businesses. Access bulk orders, volume pricing, and priority support.
                </p>
                <p className="text-xs text-orange-600 mt-2 font-medium">
                  Requires company verification
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-sky-600 hover:underline font-medium"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (step === 'contact') {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx border border-gray-100 max-w-md mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setStep('type')}
            className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 text-sm"
          >
            ← Back
          </button>
          <div className="flex items-center gap-2 mb-2">
            {formData.customerType === 'corporate' ? (
              <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                Corporate Account
              </span>
            ) : (
              <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded-full text-xs font-medium">
                Individual Account
              </span>
            )}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Information</h3>
          <p className="text-gray-600">Step 1: Enter your contact details</p>
        </div>

        <form onSubmit={handleContactSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="form-label">
              {formData.customerType === 'corporate' ? 'Business Email' : 'Email Address'}
            </label>
            <input
              type="email"
              id="email"
              required
              className="form-input"
              placeholder={formData.customerType === 'corporate' ? 'contact@company.com' : 'your@email.com'}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="phone" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              required
              className="form-input"
              placeholder="+234..."
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Include country code (e.g., +234...)
            </p>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full mt-6">
            {loading ? 'Sending Codes...' : 'Continue'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-gray-600">
          <p>We'll send verification codes to verify your contact information</p>
        </div>
      </div>
    );
  }

  if (step === 'verify' && sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🔐</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Contact</h2>
            <p className="text-gray-600">
              We sent the same 6-digit code to both:
            </p>
            <div className="mt-3 space-y-1">
              <p className="font-semibold text-gray-900">
                📧 {formData.email.replace(/(.{2})(.*)(@.*)/, '$1***$3')}
              </p>
              <p className="font-semibold text-gray-900">
                📱 {formData.phone.replace(/(\+\d{3})(\d{7})(\d{3})/, '$1****$3')}
              </p>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              Enter the code from either your email or phone
            </p>
          </div>

          <OTPVerification
            sessionId={sessionId}
            verificationType="unified"
            contactValue={`${formData.email} and ${formData.phone}`}
            onVerified={handleVerified}
            onResend={handleResendOTP}
          />
        </div>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx border border-gray-100 max-w-md mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="text-green-500 text-4xl">✓</div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Verification Complete!
          </h3>
          <p className="text-gray-600 text-center">
            Step 2: Complete your {formData.customerType === 'corporate' ? 'company' : ''} registration
          </p>
        </div>

        <form onSubmit={handleCompleteRegistration} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx-lg text-sm">
              {error}
            </div>
          )}

          {formData.customerType === 'corporate' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-orange-800 mb-3">Company Information</h4>
              <div className="space-y-3">
                <div>
                  <label htmlFor="companyName" className="form-label">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    required
                    className="form-input"
                    placeholder="Your Company Ltd"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="companyRegistrationNumber" className="form-label">
                    CAC Registration Number *
                  </label>
                  <input
                    type="text"
                    id="companyRegistrationNumber"
                    required
                    className="form-input"
                    placeholder="RC123456"
                    value={formData.companyRegistrationNumber}
                    onChange={(e) => setFormData({ ...formData, companyRegistrationNumber: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your Corporate Affairs Commission registration number
                  </p>
                </div>

                <div>
                  <label htmlFor="companyAddress" className="form-label">
                    Business Address *
                  </label>
                  <textarea
                    id="companyAddress"
                    required
                    rows={2}
                    className="form-input"
                    placeholder="123 Business Street, Lagos, Nigeria"
                    value={formData.companyAddress}
                    onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">
              {formData.customerType === 'corporate' ? 'Contact Person Details' : 'Personal Information'}
            </h4>
            <div className="space-y-3">
              <div>
                <label htmlFor="firstName" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  required
                  className="form-input"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="lastName" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  required
                  className="form-input"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              required
              minLength={6}
              className="form-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              required
              minLength={6}
              className="form-input"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full mt-6">
            {loading ? 'Creating Account...' : formData.customerType === 'corporate' ? 'Continue to Documents' : 'Complete Registration'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <span className="text-green-500">✓</span> Email verified
            <span className="text-green-500 ml-3">✓</span> Phone verified
          </p>
        </div>
      </div>
    );
  }

  if (step === 'corporate-docs') {
    return (
      <div className="bg-white p-8 rounded-volexx-lg shadow-volexx border border-gray-100 max-w-md mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="text-orange-500 text-4xl">📄</div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Upload Company Documents
          </h3>
          <p className="text-gray-600 text-center">
            Final step: Upload your company verification documents
          </p>
        </div>

        <form onSubmit={handleDocumentUpload} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx-lg text-sm">
              {error}
            </div>
          )}

          {uploadProgress && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-volexx-lg text-sm text-center">
              {uploadProgress}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="form-label">CAC Registration Certificate *</label>
              <div
                onClick={() => regDocInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${registrationDocFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-sky-400 hover:bg-sky-50'}`}
              >
                <input
                  ref={regDocInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => setRegistrationDocFile(e.target.files?.[0] || null)}
                />
                {registrationDocFile ? (
                  <div className="text-green-600">
                    <span className="text-2xl">✓</span>
                    <p className="font-medium mt-2">{registrationDocFile.name}</p>
                    <p className="text-sm text-gray-500">Click to change</p>
                  </div>
                ) : (
                  <>
                    <span className="text-3xl text-gray-400">📋</span>
                    <p className="mt-2 text-gray-600">Click to upload CAC certificate</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, or PNG (Max 5MB)</p>
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="form-label">Company Logo *</label>
              <div
                onClick={() => logoInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${logoFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-sky-400 hover:bg-sky-50'}`}
              >
                <input
                  ref={logoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.svg"
                  className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                {logoFile ? (
                  <div className="text-green-600">
                    <span className="text-2xl">✓</span>
                    <p className="font-medium mt-2">{logoFile.name}</p>
                    <p className="text-sm text-gray-500">Click to change</p>
                  </div>
                ) : (
                  <>
                    <span className="text-3xl text-gray-400">🖼️</span>
                    <p className="mt-2 text-gray-600">Click to upload company logo</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, or SVG (Max 2MB)</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !registrationDocFile || !logoFile}
            className="btn-primary w-full"
          >
            {loading ? 'Uploading...' : 'Submit for Review'}
          </button>
        </form>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 text-sm mb-2">What happens next?</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Our team will review your documents</li>
            <li>• Approval usually takes 1-2 business days</li>
            <li>• You'll receive an email once approved</li>
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

export default CustomerRegister;
